import { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { Icons } from "./Icons";

/**
 * Lightweight in-app tour. Each step targets an element by data-tour
 * attribute (`data-tour="modules"`) and shows a callout next to it. If
 * the target isn't on screen the step renders as a centred dialog so
 * the tour never gets stuck.
 *
 * Persistence is per-user via a localStorage key tagged by user id —
 * dismissing on one device doesn't suppress the tour for a teammate
 * who's signed in on the same workstation. Admins / super admins can
 * relaunch via Onboarding.tsx.
 */

interface TourStep {
  target?: string;          // data-tour attribute value, e.g. "modules"
  title: string;
  body: string;
  /** Where to anchor the callout relative to the target. */
  placement?: "right" | "bottom" | "left" | "top";
}

const STEPS: TourStep[] = [
  {
    title: "Welcome to Control",
    body:
      "This is the unified Jowcey workspace — water compliance, vehicles, support tickets and more, all sharing the same sites and people. Take 60 seconds to learn the layout.",
  },
  {
    target: "modules",
    placement: "right",
    title: "Modules live here",
    body:
      "The sidebar groups everything by module. Each one has its own dashboard, lists and detail screens. The badge counts mean something needs your attention.",
  },
  {
    target: "search",
    placement: "bottom",
    title: "Jump anywhere with ⌘K",
    body:
      "The command palette is the fastest way around — search assets, sites, work orders, tickets, customers. Hit ⌘K from any screen.",
  },
  {
    target: "tenant",
    placement: "bottom",
    title: "Switch organisations",
    body:
      "If you work across multiple tenants, switch from here. Everything you see — sites, vehicles, tickets — belongs to the active tenant.",
  },
  {
    target: "notifications",
    placement: "bottom",
    title: "Stay in the loop",
    body:
      "The bell shows real-time alerts: new tickets, non-compliant readings, overdue work. Click to expand.",
  },
  {
    title: "You're all set",
    body:
      "Admins can relaunch this tour any time from Onboarding. Have a good shift.",
  },
];

interface TourCtx {
  start: () => void;
  active: boolean;
}

const TourContext = createContext<TourCtx>({ start: () => {}, active: false });

export function useTour() { return useContext(TourContext); }

/**
 * Get the per-user storage key. Falls back to a shared key if there's
 * no user yet — that's fine; the worst case is one extra dismissal.
 */
function storageKey(userId: number | null | undefined): string {
  return `control.tour.completed.${userId ?? "anon"}`;
}

export function TourProvider({
  userId, autostart, children,
}: {
  userId: number | null | undefined;
  autostart: boolean;   // first-login auto-start
  children: ReactNode;
}) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-start once on first login (no completion marker yet).
  useEffect(() => {
    if (!autostart || !userId) return;
    if (localStorage.getItem(storageKey(userId))) return;
    // Defer a tick so target elements have rendered.
    const t = setTimeout(() => { setStep(0); setActive(true); }, 600);
    return () => clearTimeout(t);
  }, [autostart, userId]);

  const start = () => { setStep(0); setActive(true); };
  const next = () => {
    if (step + 1 >= STEPS.length) finish();
    else setStep(step + 1);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const finish = () => {
    localStorage.setItem(storageKey(userId), new Date().toISOString());
    setActive(false);
    setStep(0);
  };

  return (
    <TourContext.Provider value={{ start, active }}>
      {children}
      {active && <TourOverlay step={STEPS[step]} index={step} total={STEPS.length}
                              onNext={next} onBack={back} onFinish={finish} onSkip={finish} />}
    </TourContext.Provider>
  );
}

function TourOverlay({
  step, index, total, onNext, onBack, onFinish, onSkip,
}: {
  step: TourStep;
  index: number;
  total: number;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  onSkip: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Re-measure when the step changes or the window resizes — element
  // positions can shift if the user opens/closes the sidebar mid-tour.
  useLayoutEffect(() => {
    const measure = () => {
      if (!step.target) { setRect(null); return; }
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (!el) { setRect(null); return; }
      setRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step.target]);

  // Position the callout next to the target. Falls back to centre if
  // we can't find the element.
  const calloutStyle = (() => {
    if (!rect) {
      return {
        position: "fixed" as const,
        left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const margin = 14;
    const placement = step.placement ?? "right";
    const base: React.CSSProperties = { position: "fixed" };
    if (placement === "right") return { ...base, top: rect.top, left: rect.right + margin };
    if (placement === "left")  return { ...base, top: rect.top, left: Math.max(8, rect.left - 360 - margin) };
    if (placement === "bottom") return { ...base, top: rect.bottom + margin, left: Math.max(8, rect.left) };
    return { ...base, top: Math.max(8, rect.top - 200), left: Math.max(8, rect.left) };
  })();

  const isLast = index === total - 1;

  return (
    <>
      {/* Soft backdrop with a cut-out for the target so the user sees
          where the callout is pointing without losing the page chrome. */}
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0, 0, 0, 0.45)",
        zIndex: 1000,
        pointerEvents: "auto",
      }} onClick={onSkip} />
      {rect && (
        <div style={{
          position: "fixed",
          top: rect.top - 4, left: rect.left - 4,
          width: rect.width + 8, height: rect.height + 8,
          border: "2px solid var(--green-500)",
          borderRadius: 10,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
          zIndex: 1001,
          pointerEvents: "none",
        }} />
      )}

      <div className="card" style={{
        ...calloutStyle,
        zIndex: 1002,
        width: 360,
        padding: 16,
        background: "var(--warm-card)",
        border: "1px solid var(--warm-border-strong)",
        boxShadow: "var(--shadow-lg)",
      }}>
        <div className="kicker" style={{ marginBottom: 4 }}>Tour · {index + 1} of {total}</div>
        <h3 style={{ fontSize: 16, margin: "0 0 6px" }}>{step.title}</h3>
        <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>{step.body}</p>
        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onSkip}>Skip</button>
          {index > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={onBack}>
              <Icons.ChevLeft size={13} />Back
            </button>
          )}
          <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }}
                  onClick={isLast ? onFinish : onNext}>
            {isLast ? <><Icons.Check size={13} />Finish</> : <>Next<Icons.ChevRight size={13} /></>}
          </button>
        </div>
      </div>
    </>
  );
}
